import React, {
  createContext,
  useCallback, useContext, useMemo, useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { ConfirmFn, ConfirmOptions } from './types'
import ConfirmDialog from './ConfirmDialog'

type ConfirmContextValue = { confirm: ConfirmFn }
const ConfirmContext = createContext<ConfirmContextValue | null>(null)

type ConfirmState = {
  open: boolean
  options: ConfirmOptions
  resolve: ((value: boolean) => void) | null
}

export function ConfirmProvider({ children } : Readonly<{ children: React.ReactNode }>) {
  const [state, setState] = useState<ConfirmState>({
    open: false,
    options: {},
    resolve: null,
  })
  const { t } = useTranslation()

  const defaultOptions = useMemo<ConfirmOptions>(() => ({
    title: t('messages.confirmation'),
    cancelText: t('buttons.cancel'),
    closeOnBackdrop: true,
    closeOnEscape: true,
    confirmOnEnter: true,
  }), [t])

  const confirm = useCallback<ConfirmFn>((options = {}) => new Promise((resolve) => {
    setState({
      open: true,
      options: { ...defaultOptions, ...options },
      resolve,
    })
  }), [defaultOptions])

  const close = useCallback((value: boolean) => {
    setState((s) => {
      s.resolve?.(value)
      return { open: false, options: {}, resolve: null }
    })
  }, [])

  const ctxValue = useMemo<ConfirmContextValue>(() => ({ confirm }), [confirm])

  return (
    <ConfirmContext.Provider value={ctxValue}>
      {children}
      <ConfirmDialog
        open={state.open}
        {...state.options}
        onCancel={() => close(false)}
        onConfirm={() => close(true)}
      />
    </ConfirmContext.Provider>
  )
}

export function useConfirm() {
  const ctx = useContext(ConfirmContext)
  if (!ctx) throw new Error('useConfirm must be used within a ConfirmProvider')
  return ctx
}

